import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import { ImportManagementService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/utils/navigationRoutes";
import { ImportRecord } from "@/utils/types/importManagement";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { t } from "i18next";
import { Eye, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const ImportListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  // Use React Query for data fetching
  const {
    data: importListData,
    isLoading: importListLoading,
    refetch: refetchImportList,
  } = useQuery({
    queryKey: ["imports", "list", page, pageSize],
    queryFn: () => ImportManagementService.getImportList(page, pageSize),
  });

  // Handle view import details
  const handleViewDetails = (record: ImportRecord) => {
    navigate(navigationRoutes.IMPORT_DETAIL(record.id));
  };

  // Handle new import - redirect directly to add page
  const handleNewImport = () => {
    navigate(navigationRoutes.IMPORT_ADD);
  };

  const handleDelete = async (id: string) => {
    await ImportManagementService.deleteImportRecord(id);
    queryClient.invalidateQueries({
      queryKey: ["imports", "list", page, pageSize],
    });
    refetchImportList();
  };

  // Table columns configuration
  const columns: ProColumns<ImportRecord>[] = [
    {
      title: "Schema Name",
      dataIndex: "schema_name",
      key: "schema_name",
      sorter: (a, b) => a.schema_name.localeCompare(b.schema_name),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="view"
          onClick={() => handleViewDetails(record)}
          icon={<Eye size={16} />}
          type="text"
          title="View Import Details"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this import?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Import"
          />
        </Popconfirm>,
      ],
    },
  ];

  // Toolbar render function
  const toolBarRender = () => [
    <CustomButton
      key="add"
      type="primary"
      onClick={handleNewImport}
      title="Add Import"
    >
      <Plus size={16} className="mr-1" /> {t("Add Import")}
    </CustomButton>,
  ];

  return (
    <div className="px-4 sm:px-0">
      <BaseTable<ImportRecord>
        loading={importListLoading}
        columns={columns}
        dataSource={importListData?.results?.data ?? []}
        page={page}
        pageSize={pageSize}
        total={importListData?.count ?? 0}
        rowKey="id"
        toolBarRender={toolBarRender}
        headerTitle="Import History"
        onPageChange={(newPage, newSize) => {
          setPage(newPage);
          searchParams.set("page", newPage.toString());
          searchParams.set("pageSize", newSize.toString());
          navigate(`${navigationRoutes.IMPORTS}?${searchParams.toString()}`);
          setPageSize(newSize ?? 10);
        }}
        request={() => refetchImportList()}
        tableName={gridName.importTable}
      />
    </div>
  );
};

export default ImportListPage;
